// Environments (design/isomorphic environments): every environment of every organisation,
// with its limits against the organisation's quota, its network and its live runs.
import { Cube } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import type { Environment, Organisation } from "../api/client";
import { environmentsQuery, orgsQuery, runsQuery, workflowsQuery } from "../api/queries";
import { type Messages, useT } from "../i18n";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { type Column, DataTable } from "../ui/DataTable";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Mono, Pill } from "../ui/status";
import { networkQuery } from "../workloads/api";
import { instanceName, liveRuns } from "../workloads/environment/derive";
import { Allocation, EnvironmentStatus } from "../workloads/environment/marks";
import { gibCount } from "../workloads/format";
import { sectionCrumbs } from "../layout/nav";

function NetworkSummary({ env }: { env: Environment }) {
  const t = useT().workloads.environments;
  const network = useQuery({ ...networkQuery(env.network_id ?? ""), enabled: env.network_id != null });
  const vpn = env.network_id == null ? t.noVpn : network.data ? t.vpnOf(network.data.vpn_type, network.data.vpn_required) : t.vpn;
  return (
    <div className="text-[13px]">
      <Mono>{env.base_image}</Mono>
      <div className="text-xs text-gray-500">
        {vpn} · {t.egressDomains(env.egress_allowlist.length)}
      </div>
    </div>
  );
}

function columns(w: Messages["workloads"], org: (e: Environment) => Organisation | undefined, live: (e: Environment) => number): Column<Environment>[] {
  const c = w.environments.columns;
  return [
    {
      key: "name",
      header: c.environment,
      cell: (e) => (
        <Link to={`/environments/${e.id}`} className="group block">
          <span className="block font-semibold text-gray-900 group-hover:text-primary">
            {org(e)?.name ?? "—"} / {e.name}
          </span>
          <Mono className="text-[11.5px] text-gray-500">{instanceName(e, org(e))}</Mono>
        </Link>
      ),
    },
    { key: "status", header: c.status, cell: (e) => <EnvironmentStatus status={e.status} /> },
    { key: "cpu", header: c.cpu, cell: (e) => <Allocation value={e.limits_cpu} of={org(e)?.quota_cpu} unit={w.cores} />, hideBelow: "md" },
    {
      key: "mem",
      header: c.memory,
      cell: (e) => {
        const quota = org(e)?.quota_memory_bytes;
        return <Allocation value={gibCount(e.limits_memory_bytes)} of={quota === undefined ? undefined : gibCount(quota)} unit="GiB" />;
      },
      hideBelow: "lg",
    },
    { key: "network", header: c.network, cell: (e) => <NetworkSummary env={e} />, hideBelow: "xl" },
    { key: "runs", header: c.runs, cell: (e) => <Pill tone={live(e) ? "blue" : "gray"}>{w.environments.live(live(e))}</Pill>, hideBelow: "sm" },
  ];
}

export default function EnvironmentsPage() {
  const { workloads } = useT();
  const t = workloads.environments;
  const environments = useQuery(environmentsQuery());
  const orgs = useQuery(orgsQuery());
  const workflows = useQuery(workflowsQuery());
  const runs = useQuery(runsQuery());
  const org = (e: Environment) => orgs.data?.find((o) => o.id === e.org_id);
  const live = (e: Environment) => liveRuns(e, workflows.data ?? [], runs.data ?? []);

  return (
    <>
      <PageHeader
        title={t.title}
        breadcrumb={sectionCrumbs("environments")}
        description={t.description}
      />
      {environments.isPending ? <Loading /> : null}
      {environments.isError ? <ErrorState message={environments.error.message} /> : null}
      {environments.data?.length === 0 ? (
        <EmptyState icon={<Cube weight="duotone" />} title={t.emptyTitle}>
          {t.emptyBody}
        </EmptyState>
      ) : null}
      {environments.data && environments.data.length > 0 ? (
        <div className={cn(cardClass, "p-3 lg:p-4")}>
          <DataTable label={t.title} columns={columns(workloads, org, live)} rows={environments.data} rowKey={(e) => e.id} />
        </div>
      ) : null}
    </>
  );
}
